import ArrowDownIcon from "@/assets/icons/arrow-down.svg";
import { CustomModelModal } from "@/component/ChatBox/CustomModelModal";
import { useAllowedModels } from "@/hooks/useAllowedModels";
import { firstPurchasePromoStore } from "@/store/firstPurchasePromo";
import { userStore } from "@/store/user";
import { Resolution } from "@/types/common";
import {
  ModelConfigKey,
  ModelPreset,
  filterNullValues,
  getPresetConfig,
} from "@/types/modelConfig";
import { Tooltip } from "antd";
import classNames from "classnames";
import { observer } from "mobx-react-lite";
import { useRouter } from "next/navigation";
import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { VidMuseEvent } from "../../common/events";
import { logger } from "../../common/logger";
import { formatNumberWithCommas } from "../../util/formatNumber";
import { UpgradeTip } from "./UpgradeTip";
import CustomIcon from "./assets/model-custom.svg";
import LiteIcon from "./assets/model-lite.svg";
import StudioIcon from "./assets/model-studio.svg";
import SettingsIcon from "./assets/settings.svg";

interface ModelSelectorProps {
  value: ModelPreset;
  onChange: (value: ModelPreset) => void;
  onCustomConfigChange: (
    config: Partial<Record<ModelConfigKey, string>>,
  ) => void;
  customConfig?: Partial<Record<ModelConfigKey, string>>;
  disabled?: boolean;
  resolution?: Resolution;
}

export const ModelSelector = observer(function ModelSelector({
  value,
  onChange,
  onCustomConfigChange,
  customConfig = {},
  disabled = false,
  resolution = Resolution.HD,
}: ModelSelectorProps) {
  const router = useRouter();
  const [isOpen, setIsOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [dropdownPos, setDropdownPos] = useState({
    top: 0,
    left: 0,
    bottom: 0,
  });

  const triggerRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Fetch model configurations to calculate prices
  const { data: modelsData } = useAllowedModels();

  // Auto-switch from custom to studio for free users
  useEffect(() => {
    if (userStore.isFreeUser && value === "custom") {
      // Apply studio preset configuration
      const presetConfig = getPresetConfig("studio");
      if (presetConfig) {
        onCustomConfigChange(filterNullValues(presetConfig));
      }
      onChange("studio");
    }
  }, [value, onChange, onCustomConfigChange]);

  // Handle outside click and close on scroll/resize
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (triggerRef.current?.contains(target)) return;
      if (dropdownRef.current?.contains(target)) return;
      setIsOpen(false);
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      window.addEventListener("scroll", () => setIsOpen(false), true);
      window.addEventListener("resize", () => setIsOpen(false));
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("scroll", () => setIsOpen(false), true);
      window.removeEventListener("resize", () => setIsOpen(false));
    };
  }, [isOpen]);

  const handleToggle = () => {
    if (!isOpen && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const dropdownWidth = 380; // Width of dropdown
      const buttonCenter = rect.left + rect.width / 2;
      const centeredLeft = buttonCenter - dropdownWidth / 2;

      setDropdownPos({
        left: centeredLeft,
        top: 0,
        bottom: window.innerHeight - rect.top + 8, // 8px gap above button
      });
    }
    setIsOpen(!isOpen);
  };

  const handleModeSelect = (newValue: ModelPreset) => {
    if (newValue === "custom") {
      // Block custom for free users
      if (userStore.isFreeUser) {
        logger.event(VidMuseEvent.FreeUserClickCustomModel);
        // 显示首单优惠弹窗，点击 Claim 后跳转到 Pricing 页面
        firstPurchasePromoStore.tryShow(() => {
          router.push("/pricing");
        });
        setIsOpen(false);
        return;
      }
      // Pre-fill custom modal with values:
      // - If already on custom, keep existing custom config (no change needed)
      // - If switching from studio/lite, use that preset's values as starting point
      if (value !== "custom") {
        const currentPresetConfig = getPresetConfig(value);
        if (currentPresetConfig) {
          onCustomConfigChange(filterNullValues(currentPresetConfig));
        }
      }
      // Note: If already on custom, customConfig already has the right values
      setModalOpen(true);
      setIsOpen(false);
    } else {
      // Apply preset configuration for Studio or Lite
      const presetConfig = getPresetConfig(newValue);
      if (presetConfig) {
        onCustomConfigChange(filterNullValues(presetConfig));
      }
      onChange(newValue);
      setIsOpen(false);
    }
  };

  const handleModalClose = () => {
    setModalOpen(false);
  };

  const handleModalConfirm = (
    config: Partial<Record<ModelConfigKey, string>>,
  ) => {
    // Merge custom selections with default null config for unsupported models
    const defaultConfig = getPresetConfig("custom") || {};
    const finalConfig = { ...defaultConfig, ...config };
    onCustomConfigChange(
      finalConfig as Partial<Record<ModelConfigKey, string>>,
    );
    onChange("custom");
    setModalOpen(false);
  };

  const getIcon = (preset: ModelPreset) => {
    const Icon = {
      studio: StudioIcon,
      lite: LiteIcon,
      custom: CustomIcon,
    }[preset];

    return <Icon className="w-5 h-5" />;
  };

  const getCheckmark = (preset: ModelPreset) => {
    if (value !== preset) return null;

    const colorClass = {
      studio: "text-cyan-400",
      lite: "text-yellow-400",
      custom: "text-pink-400",
    }[preset];

    return (
      <svg className={`w-3 h-3 ${colorClass}`} viewBox="0 0 12 12" fill="none">
        <path
          d="M10 3L4.5 8.5L2 6"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  };

  const getLabel = (preset: ModelPreset) => {
    switch (preset) {
      case "studio":
        return "Studio";
      case "lite":
        return "Lite";
      case "custom":
        return "Custom";
    }
  };

  const getDescription = (preset: ModelPreset) => {
    switch (preset) {
      case "studio":
        return "High-end visuals for official releases";
      case "lite":
        return "Fast, cost-effective for social shorts";
      case "custom":
        return "Full control over models & parameters";
    }
  };

  // Calculate total price for a preset based on resolution
  const calculatePresetPrice = useMemo(() => {
    if (!modelsData) return null;

    return (preset: ModelPreset): string | null => {
      if (preset === "custom") return null;

      const presetConfig = getPresetConfig(preset);
      if (!presetConfig) return null;

      let totalPrice = 0;
      const resolutionStr = resolution; // "720p" or "1080p"

      // Helper to find model price by name and resolution
      const getModelPrice = (
        modelKey: ModelConfigKey,
        modelName: string | null,
      ): number => {
        if (!modelName) return 0;

        const modelsList = modelsData[modelKey];
        if (!modelsList) return 0;

        const model = modelsList.find((m) => m.name === modelName);
        if (!model || !model.price_items || model.price_items.length === 0) {
          return 0;
        }

        // Find price item matching the current resolution
        // Check properties.resolution for video models
        let priceItem = model.price_items.find(
          (item) => item.properties?.resolution === resolutionStr,
        );

        // If no resolution match found, use first price item with empty properties (default)
        // or the first price item overall as fallback
        if (!priceItem) {
          // Try to find default (empty properties)
          priceItem = model.price_items.find(
            (item) =>
              !item.properties || Object.keys(item.properties).length === 0,
          );

          // If still no match, use first price item
          if (!priceItem) {
            priceItem = model.price_items[0];
          }
        }

        if (!priceItem) return 0;

        // Convert price per unit to price per minute
        // For videos: price is per second, so multiply by 60
        // For images: price is per image, estimate ~4 images per minute
        // For audio: price is per second, so multiply by 60
        if (priceItem.unit_type === "seconds") {
          return priceItem.price.output * 60;
        } else if (priceItem.unit_type === "images") {
          return priceItem.price.output * 4; // Estimate 4 images per minute
        }

        return priceItem.price.output;
      };

      // Calculate prices for all models in the preset
      Object.entries(presetConfig).forEach(([key, modelName]) => {
        totalPrice += getModelPrice(key as ModelConfigKey, modelName);
      });

      return totalPrice > 0
        ? `~${formatNumberWithCommas(Math.round(totalPrice))} Cr/min`
        : null;
    };
  }, [modelsData, resolution]);

  const getPrice = (preset: ModelPreset) => {
    if (!calculatePresetPrice) return null;
    return calculatePresetPrice(preset);
  };

  // Get model display names for a preset
  const getPresetModelDetails = (preset: ModelPreset) => {
    const config = getPresetConfig(preset);
    if (!config || !modelsData) return [];

    const details: Array<{ label: string; displayName: string }> = [];

    // Image Generation (t2i/i2i)
    if (config.t2i_model_name && config.i2i_model_name) {
      const t2iModel = modelsData.t2i_model_name?.find(
        (m) => m.name === config.t2i_model_name,
      );
      if (t2iModel) {
        details.push({
          label: "Image Generation",
          displayName: t2iModel.display_name,
        });
      }
    }

    // Video Generation (i2v)
    if (config.i2v_model_name) {
      const i2vModel = modelsData.i2v_model_name?.find(
        (m) => m.name === config.i2v_model_name,
      );
      if (i2vModel) {
        details.push({
          label: "Video Generation",
          displayName: i2vModel.display_name,
        });
      }
    }

    // Avatar Generation
    if (config.avatar_model_name) {
      const avatarModel = modelsData.avatar_model_name?.find(
        (m) => m.name === config.avatar_model_name,
      );
      if (avatarModel) {
        details.push({
          label: "Avatar Generation",
          displayName: avatarModel.display_name,
        });
      }
    }

    return details;
  };

  // Render tooltip content for preset details
  const renderPresetTooltip = (preset: ModelPreset) => {
    const details = getPresetModelDetails(preset);
    if (details.length === 0) return null;

    return (
      <div className="space-y-1.5">
        {details.map((detail, index) => (
          <div key={index} className="flex flex-col gap-0.5">
            <span className="text-[10px] text-neutral-400 uppercase tracking-wide">
              {detail.label}
            </span>
            <span className="text-xs text-white font-medium">
              {detail.displayName}
            </span>
          </div>
        ))}
      </div>
    );
  };

  // Info icon component
  const InfoIcon = () => (
    <svg
      className="w-3.5 h-3.5"
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <circle
        cx="8"
        cy="8"
        r="7"
        stroke="currentColor"
        strokeWidth="1.5"
        fill="none"
      />
      <path
        d="M8 7.5V11.5M8 5.5V5.51"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );

  return (
    <div className="relative">
      {/* Trigger Button */}
      <button
        ref={triggerRef}
        onClick={handleToggle}
        disabled={disabled}
        className={classNames(
          "flex items-center h-10 rounded-full transition-all duration-200",
          "bg-[rgba(33,33,41,0.70)] border border-[rgba(255,255,255,0.05)]",
          "hover:border-[rgba(255,255,255,0.05)] focus:outline-none",
          "px-2 md:px-3", // Icon-only on mobile/tablet, full width on desktop
          "justify-center md:justify-between gap-0 md:gap-1", // Center icon on mobile/tablet, space-between on desktop
          {
            "border-[rgba(255,255,255,0.05)]": isOpen,
            "opacity-50 cursor-not-allowed": disabled,
          },
        )}
      >
        <span className="flex items-center gap-1 text-sm font-normal text-white">
          {getIcon(value)}
          <span className="hidden md:inline">{getLabel(value)}</span>
        </span>
        <ArrowDownIcon
          className={classNames(
            "w-4 h-4 text-text-primary transition-transform duration-200 hidden md:block",
            { "rotate-180": isOpen },
          )}
        />
      </button>

      {/* Dropdown Menu (Portal) */}
      {isOpen &&
        createPortal(
          <div
            ref={dropdownRef}
            style={{
              position: "fixed",
              left: dropdownPos.left,
              bottom: dropdownPos.bottom,
              zIndex: 9999,
            }}
            className="w-[380px] bg-[#121212] border border-white/10 rounded-xl shadow-2xl backdrop-blur-xl overflow-hidden ring-1 ring-black/50"
          >
            <div className="p-1">
              {/* Studio Option */}
              <button
                onClick={() => handleModeSelect("studio")}
                className="w-full text-left p-3 rounded-lg hover:bg-white/5 flex items-start justify-between group transition-colors"
              >
                <div className="flex items-start gap-1 flex-1">
                  {getIcon("studio")}
                  <div className="flex-1">
                    <div className="text-sm font-medium text-white flex items-center gap-2">
                      {getLabel("studio")}
                      {getCheckmark("studio")}
                    </div>
                    <div className="text-xs text-neutral-500 mt-0.5 leading-snug">
                      {getDescription("studio")}
                    </div>
                  </div>
                </div>
                <Tooltip
                  title={renderPresetTooltip("studio")}
                  placement="top"
                  color="#1f1f1f"
                  zIndex={10000}
                >
                  <span
                    onClick={(e) => e.stopPropagation()}
                    className="text-neutral-500 hover:text-neutral-300 transition-colors cursor-help inline-flex ml-2 mt-0.5"
                  >
                    <InfoIcon />
                  </span>
                </Tooltip>
              </button>

              {/* Lite Option */}
              <button
                onClick={() => handleModeSelect("lite")}
                className="w-full text-left p-3 rounded-lg hover:bg-white/5 flex items-start justify-between group transition-colors"
              >
                <div className="flex items-start gap-1 flex-1">
                  {getIcon("lite")}
                  <div className="flex-1">
                    <div className="text-sm font-medium text-white flex items-center gap-2">
                      {getLabel("lite")}
                      {getCheckmark("lite")}
                    </div>
                    <div className="text-xs text-neutral-500 mt-0.5 leading-snug">
                      {getDescription("lite")}
                    </div>
                  </div>
                </div>
                <Tooltip
                  title={renderPresetTooltip("lite")}
                  placement="top"
                  color="#1f1f1f"
                  zIndex={10000}
                >
                  <span
                    onClick={(e) => e.stopPropagation()}
                    className="text-neutral-500 hover:text-neutral-300 transition-colors cursor-help inline-flex ml-2 mt-0.5"
                  >
                    <InfoIcon />
                  </span>
                </Tooltip>
              </button>
            </div>

            {/* Divider */}
            <div className="h-px bg-white/10 mx-2 my-1"></div>

            <div className="p-1">
              {/* Custom Option */}
              <div className={classNames("relative rounded-lg group")}>
                <button
                  onClick={() => handleModeSelect("custom")}
                  className={classNames(
                    "w-full text-left p-3 rounded-lg flex items-start justify-between transition-colors",
                    {
                      "hover:bg-white/5": !userStore.isFreeUser,
                    },
                  )}
                >
                  <div
                    className={classNames("flex items-start gap-1", {
                      "opacity-60": userStore.isFreeUser,
                    })}
                  >
                    {getIcon("custom")}
                    <div>
                      <div className="text-sm font-medium text-white flex items-center gap-2">
                        {getLabel("custom")}
                        {getCheckmark("custom")}
                      </div>
                      <div className="text-xs text-neutral-500 mt-0.5 leading-snug">
                        {userStore.isFreeUser
                          ? "Upgrade to Pro to customize."
                          : getDescription("custom")}
                      </div>
                    </div>
                  </div>

                  {userStore.isFreeUser && <UpgradeTip className="mt-2" />}
                </button>

                {/* Settings Gear for Quick Access - Only for Pro users */}
                {!userStore.isFreeUser && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setModalOpen(true);
                      setIsOpen(false);
                    }}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-2 text-neutral-500 hover:text-white hover:bg-white/10 rounded-md transition-colors"
                    title="Configure Custom Settings"
                  >
                    <SettingsIcon className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          </div>,
          document.body,
        )}

      {/* Configuration Modal */}
      <CustomModelModal
        open={modalOpen}
        onClose={handleModalClose}
        onConfirm={handleModalConfirm}
        initialConfig={customConfig}
        resolution={resolution}
      />
    </div>
  );
});
